import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { Modal } from "../Modal/Modal";
import {
  PrimaryButtonMediumV2,
  SecondaryButtonMediumV2,
} from "../Buttons/Buttons";
import {
  TextBaseSemibold,
  TextMdRegular,
  TextSmRegular,
  TextXlMedium,
} from "../Typography/TypographyV2";
import { Loader } from "../Loader/Loader";
import { LoaderContainer } from "../../pages/public/pages.public.util";
import { useStoreState } from "../../util/util";
import type {
  AdvancedFilterCard,
  AdvancedFilterCollectionInstance,
} from "../../types/types";
import { AdvancedFiltersBrowsePane } from "./AdvancedFiltersBrowsePane";
import { AdvancedFiltersCriterionCard } from "./AdvancedFiltersCriterionCard";
import {
  advancedFilterCardKey,
  findAdvancedFilterCardIndex,
  useAdvancedFilterCollectionInstances,
} from "./advancedFiltersUtils";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 75vh;
`;

const ModalTitleRow = styled.div`
  padding-bottom: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const PanesRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.resolve("color_bg_surface_subtle")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

const CardsColumn = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const EmptyCardsState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_5xl")} 0;
  width: 100%;
  text-align: center;
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding-top: ${({ theme }) => theme.resolve("spacing_lg")};
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

/**
 * Batch-apply-on-Search (FE plan §9a): edits to a card's own conditions only
 * mutate this modal's local `cards` state — nothing propagates to the
 * page's filter state (and no request is sent) until "Search" is clicked.
 */
function AdvancedFiltersModalContent({
  instances,
  initialCards,
  onClose,
  onApply,
}: {
  instances: AdvancedFilterCollectionInstance[];
  initialCards: AdvancedFilterCard[];
  onClose: () => void;
  onApply: (cards: AdvancedFilterCard[]) => void;
}) {
  const { t } = useTranslation();
  const [cards, setCards] = useState<AdvancedFilterCard[]>(initialCards);
  // An invalid numeric field clears its own criterion from `cards` (see
  // NumericFilterInput's inline mode), so `cards` alone can't reveal it —
  // track the offending fields separately to block Search.
  const [invalidFieldKeys, setInvalidFieldKeys] = useState<Set<string>>(
    () => new Set()
  );
  const hasInvalidFields = invalidFieldKeys.size > 0;

  const instancesById = new Map(
    instances.map((instance) => [instance.id, instance])
  );

  const handleToggleValue = (
    instance: AdvancedFilterCollectionInstance,
    value: string
  ) => {
    const index = findAdvancedFilterCardIndex(cards, instance.id, value);
    if (index >= 0) {
      setCards(cards.filter((_, i) => i !== index));
    } else {
      setCards([
        ...cards,
        {
          collection_instance_id: instance.id,
          key_attribute_value: value,
          quantitative: [],
          other: [],
        },
      ]);
    }
  };

  const handleChangeCard = (index: number, next: AdvancedFilterCard) => {
    setCards(cards.map((card, i) => (i === index ? next : card)));
  };

  const handleFieldInvalidChange = useCallback(
    (fieldKey: string, isInvalid: boolean) => {
      setInvalidFieldKeys((prev) => {
        if (prev.has(fieldKey) === isInvalid) return prev;
        const next = new Set(prev);
        if (isInvalid) {
          next.add(fieldKey);
        } else {
          next.delete(fieldKey);
        }
        return next;
      });
    },
    []
  );

  const handleSearch = () => {
    if (hasInvalidFields) return;
    onApply(cards);
    onClose();
  };

  // Unlike an in-card edit (batch-apply-on-Search, §9a), "Clear all" is a
  // decisive reset — it applies and closes immediately, the same as Search
  // with zero cards, rather than leaving an emptied draft that still
  // requires a second click to actually take effect.
  const handleClearAll = () => {
    setCards([]);
    onApply([]);
    onClose();
  };

  // Removing the last card is treated as "Clear all", so the page's
  // SelectedFiltersArea chip removal and this modal both end in the same state.
  const handleRemoveCard = (index: number) => {
    const remainingCards = cards.filter((_, i) => i !== index);
    if (remainingCards.length === 0) {
      handleClearAll();
      return;
    }
    setCards(remainingCards);
  };

  return (
    <ModalBody>
      <ModalTitleRow>
        <TextXlMedium>{t("Advanced Filters")}</TextXlMedium>
      </ModalTitleRow>
      <PanesRow>
        <AdvancedFiltersBrowsePane
          instances={instances}
          cards={cards}
          onToggleValue={handleToggleValue}
        />
        <CardsColumn>
          {cards.length === 0 && (
            <EmptyCardsState>
              <TextBaseSemibold>{t("No filters yet")}</TextBaseSemibold>
              <TextMdRegular>
                {t("Pick a filter on the left to get started.")}
              </TextMdRegular>
            </EmptyCardsState>
          )}
          {cards.map((card, index) => {
            const instance = instancesById.get(card.collection_instance_id);
            // Instance disappeared (deleted/invalidated) since being added to
            // this modal session — drop it from view rather than render a
            // card with no metadata to source its rows from.
            if (!instance) return null;
            const cardKey = advancedFilterCardKey(
              card.collection_instance_id,
              card.key_attribute_value
            );
            return (
              <AdvancedFiltersCriterionCard
                key={cardKey}
                instance={instance}
                card={card}
                onChange={(next) => handleChangeCard(index, next)}
                onRemove={() => handleRemoveCard(index)}
                onQuantitativeInvalidChange={(filterId, isInvalid) =>
                  handleFieldInvalidChange(`${cardKey}::${filterId}`, isInvalid)
                }
              />
            );
          })}
        </CardsColumn>
      </PanesRow>
      <FooterRow>
        {hasInvalidFields && (
          <TextSmRegular color="error">
            {t("Fix the invalid numeric values before searching.")}
          </TextSmRegular>
        )}
        <SecondaryButtonMediumV2
          testid="advanced-filters-clear-all"
          onClick={handleClearAll}
          disabled={cards.length === 0}
        >
          {t("Clear all")}
        </SecondaryButtonMediumV2>
        <PrimaryButtonMediumV2
          testid="advanced-filters-search"
          onClick={handleSearch}
          disabled={hasInvalidFields}
        >
          {t("Search")}
        </PrimaryButtonMediumV2>
      </FooterRow>
    </ModalBody>
  );
}

type AdvancedFiltersModalProps = {
  show: boolean;
  onClose: () => void;
  initialCards: AdvancedFilterCard[];
  onApply: (cards: AdvancedFilterCard[]) => void;
};

export const AdvancedFiltersModal = ({
  show,
  onClose,
  initialCards,
  onApply,
}: AdvancedFiltersModalProps) => {
  const { storefront_id } = useStoreState();
  // §6 assumes the BE's own CRUD-before-values sequencing is satisfied by
  // this point; `valid_only` filtering means an instance archived mid-session
  // simply stops appearing here on next fetch, never errors.
  const { instances, isLoading } = useAdvancedFilterCollectionInstances(
    storefront_id,
    { enabled: show }
  );

  return (
    <Modal show={show} closeModal={onClose} overlay modalWidth="95%">
      {isLoading ? (
        <LoaderContainer style={{ height: "50vh" }}>
          <Loader isLoading width={32} />
        </LoaderContainer>
      ) : (
        // `Modal` unmounts everything below it whenever `show` is false, so
        // `cards` state here is always freshly seeded from `initialCards` on
        // every open — no reset effect required.
        <AdvancedFiltersModalContent
          instances={instances}
          initialCards={initialCards}
          onClose={onClose}
          onApply={onApply}
        />
      )}
    </Modal>
  );
};

AdvancedFiltersModal.displayName = "AdvancedFiltersModal";
